import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal.js';
import { Button } from '../ui/Button.js';
import { RichTextEditor } from '../ui/RichTextEditor.js';
import { useUpdateLead, type LeadRow } from '../../hooks/useLeads.js';

interface NotesModalProps {
  lead: LeadRow;
  open: boolean;
  onClose: () => void;
}

export function NotesModal({ lead, open, onClose }: NotesModalProps) {
  const { t } = useTranslation();
  const updateLead = useUpdateLead();
  const [notes, setNotes] = useState(lead.notes ?? '');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setNotes(lead.notes ?? '');
    setConfirmDelete(false);
  }, [lead.id, lead.notes, open]);

  const hasNotes = !!(lead.notes?.trim());

  function handleSave() {
    updateLead.mutate({ id: lead.id, notes }, { onSuccess: onClose });
  }

  function handleDelete() {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    updateLead.mutate({ id: lead.id, notes: '' }, { onSuccess: onClose });
  }

  function handleClose() {
    setConfirmDelete(false);
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t('leads.notesModal.title')}
      size="md"
      footer={
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center' }}>
          <Button variant="ghost" tone="muted" size="sm" onClick={handleClose}>
            {t('leads.notesModal.cancel')}
          </Button>
          {hasNotes && (
            <Button
              variant={confirmDelete ? 'outline' : 'ghost'}
              tone="muted"
              size="sm"
              loading={updateLead.isPending && confirmDelete}
              onClick={handleDelete}
            >
              {confirmDelete ? t('leads.notesModal.confirmDelete') : t('leads.notesModal.delete')}
            </Button>
          )}
          <Button
            variant="solid"
            tone="cyan"
            size="sm"
            loading={updateLead.isPending && !confirmDelete}
            onClick={handleSave}
          >
            {t('leads.notesModal.save')}
          </Button>
        </div>
      }
    >
      <RichTextEditor
        value={notes}
        onChange={setNotes}
        placeholder={t('leads.notesModal.placeholder')}
        minHeight={200}
        toolbar={['bold', 'italic', 'underline', 'divider', 'link', 'divider', 'bulletList', 'orderedList']}
      />
    </Modal>
  );
}
